import classNames from 'classnames';
import type {
    Action,
    AuthenticationStatusResponse,
    OptionType,
    OptionValueType,
    Trigger,
} from '../types/types';
import SelectedConnector from './SelectedConnector';
import { getLocale, isNullOrEmpty, isTaskItemAuthenticated } from '../utils/utils';

import './trigger-or-action-summary.css';

type TaskItemType = 'trigger' | 'action';

const formatDate = (value: string) => {
    const hasTime = /[T ]\d{2}:|\d{1,2}:\d{2}/.test(value);

    return new Date(value).toLocaleString(getLocale(), {
        dateStyle: 'short',
        timeStyle: hasTime ? 'short' : undefined,
    });
};

const parseTemplate = (
    template: string,
    params: {
        [key: string]: {
            type: OptionType;
            value: OptionValueType;
        };
    },
) => {
    const paramRegex = /(\${.+?})/g;
    const paramTrimRegex = /([\${}])/g;
    const paramMatchRegex = /^\${(.+?)}$/;
    const tokenSplitRegex = /({.+?})/g;
    const tokenMatchRegex = /^{.*}$/;
    const tokenTrimRegex = /[{}]/g;

    // Split the template string by the pattern, keeping the delimiters.
    const parts = template.split(paramRegex);

    // Do we have all params needed for the template? Get required params from the template...
    const templateParams = parts
        .filter((tp) => paramRegex.test(tp))
        .map((tpf) => tpf.replace(paramTrimRegex, ''));
    // ...and check if template params exist and have a value in given params.
    const givenParams = Object.keys(params);
    const templateParamsExist = templateParams.every(
        (tp) =>
            givenParams.includes(tp) &&
            !isNullOrEmpty(params[tp].value) &&
            !Number.isNaN(params[tp].value),
    );

    // If all template params are there use the template otherwise skip the whole thing and
    // just show the triggerOrActionName.
    return templateParamsExist ? (
        <>
            {parts.map((part) => {
                // Check if the part matches our pattern.
                const match = part.match(paramMatchRegex);

                if (match) {
                    const paramName = match[1];
                    const paramValue = params[paramName].value!.toString();
                    const paramType = params[paramName].type;
                    // Split the value into parts based on tokens.
                    const valueParts = paramValue.split(tokenSplitRegex);
                    const maxLength = 35;
                    const truncatedParts = [];

                    let currentLength = 0;

                    // Process value parts until we hit length limit or run out of parts.
                    for (const value of valueParts) {
                        if (tokenMatchRegex.test(value)) {
                            // Add a token (always complete).
                            truncatedParts.push(
                                <em key={value}>{value.replace(tokenTrimRegex, '')}</em>,
                            );
                        } else {
                            // Add a string (shortened if necessary).
                            const isDate = paramType === 'Date' || paramType === 'DateTime';
                            const shortValue = value.substring(0, maxLength - currentLength);
                            const displayValue = isDate ? formatDate(shortValue) : shortValue;

                            truncatedParts.push(<b key={value}>{displayValue}</b>);
                        }

                        if (currentLength + value.length > maxLength) {
                            truncatedParts.push('...');
                            break; // Stop here and exit the loop.
                        }

                        currentLength += value.length;
                    }

                    return truncatedParts;
                }

                // Return non-parameter bits of the template.
                return part;
            })}
        </>
    ) : (
        <b>{params['triggerOrActionName'].value}</b>
    );
};

const TriggerOrActionSummary = ({
    itemType,
    itemData,
    onOpenConfig,
    onDeleteItem,
    isSelected,
    authenticationStatus,
    isLoadingAuthenticationStatus,
}: {
    itemType: TaskItemType;
    itemData: Trigger | Action;
    onOpenConfig?: () => void;
    onDeleteItem?: () => void;
    isSelected?: boolean;
    authenticationStatus: AuthenticationStatusResponse;
    isLoadingAuthenticationStatus: boolean;
}) => {
    const isConnectorAuthenticated = itemData
        ? isTaskItemAuthenticated(itemData, authenticationStatus)
        : false;

    const itemClasses = classNames('task-config-item ta-tile ta-tile-interactive', {
        'is-selected': isSelected,
    });

    // Generate a params object with their types and values.
    const params = Object.fromEntries(
        itemData.options.map((cfg) => [cfg.id, { type: cfg.type, value: itemData.values[cfg.id] }]),
    );

    return (
        <button
            className={itemClasses}
            type="button"
            onClick={onOpenConfig}
            data-testid={`${itemData.id}_${itemType}Button`}
        >
            <section className="connector">
                <SelectedConnector
                    itemData={itemData}
                    isConnectorAuthenticated={isConnectorAuthenticated}
                    onDelete={onDeleteItem}
                    isLoadingAuthenticationStatus={isLoadingAuthenticationStatus}
                />
            </section>

            <section className="trigger-or-action">
                {itemData.description ? (
                    <p className="text md description">
                        {parseTemplate(itemData.description, {
                            triggerOrActionName: {
                                type: 'String',
                                value: itemData.name.toLowerCase(),
                            },
                            ...params,
                        })}
                    </p>
                ) : (
                    <span className="text md description">
                        <b>{itemData.name}</b>
                    </span>
                )}
            </section>
        </button>
    );
};

export default TriggerOrActionSummary;
